import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { Entitlement } from '@caliu/types'
import { apiFetch } from '../api/client'
import { track, setProfileProps, AnalyticsEvent } from '../lib/analytics'
import { useAuthStore } from './auth'

/**
 * Caliu Pro entitlement state. The server's `GET /subscription` is the single
 * trusted source of Pro across every client (web + Swift): grandfather flag +
 * RevenueCat webhook state, which also covers purchases made on other platforms.
 * The RevenueCat SDK is used only to run checkout, never to gate features.
 *
 * The entitlement is **persisted to localStorage** (like `caliu-auth`) so it
 * survives reloads and is available offline: a grandfathered / Pro user who
 * opens the app offline reads their last known Pro state instead of being
 * downgraded to free. `apiFetch` is offline-tolerant (returns null), so a
 * failed poll leaves the persisted value untouched. Pattern: trust local,
 * verify when online.
 */
interface SubscriptionState {
  isPro: boolean
  /** True once a `GET /subscription` has ever resolved (persisted). Until then
   *  the real entitlement is unknown and features must not be gated. */
  loaded: boolean
  /** ISO 8601. null for grandfathered / lifetime / no subscription. */
  expiresAt: string | null
  willRenew: boolean
  /** Where the entitlement comes from — drives the "manage subscription" copy. */
  source: Entitlement['source']
  /** Renewing subscription vs the one-time lifetime purchase; null when no
   *  store purchase backs the entitlement (free, grandfathered, promotional).
   *  Copy only ("Renews on" vs "Lifetime access") plus the `plan` profile prop. */
  plan: Entitlement['plan']
  /** The free note cap for this account, null for unlimited (server-derived,
   *  see `Entitlement.noteLimit`). Read by the note-limit gate; never set here
   *  other than from the entitlement. */
  noteLimit: number | null
  /** The account the persisted entitlement belongs to (null before the first
   *  answer, and for state saved before this field existed). Another account's
   *  saved entitlement is dropped on sign-in, never shown. */
  ownerId: string | null
  /** Fetch `GET /subscription` and apply it. Returns the answer, or null when
   *  there was none (offline, an error, or the account changed meanwhile). */
  refresh: () => Promise<Entitlement | null>
  /** After a completed checkout: unlock now, then have the server read the
   *  store directly. A plain refresh would race the webhook and downgrade the
   *  buyer it has not heard about yet. */
  confirmPurchase: () => Promise<void>
  reset: () => void
}

interface ReconcileResponse {
  reconciled: boolean
  entitlement: Entitlement
}

/** The checkout confirmation in flight. `refresh` waits for it, so a focus or
 *  `online` refresh can't read the server before it knows about the purchase. */
let confirming: Promise<void> | null = null

export const useSubscriptionStore = create<SubscriptionState>()(
  persist(
    (set, get) => ({
      isPro: false,
      loaded: false,
      expiresAt: null,
      willRenew: false,
      source: 'none',
      plan: null,
      noteLimit: null,
      ownerId: null,
      refresh: async () => {
        if (confirming) await confirming
        const owner = useAuthStore.getState().user?.id ?? null
        const ent = await apiFetch<Entitlement>('/subscription')
        // Answered for an account that has since left: never apply it.
        if (!ent || !owner || owner !== useAuthStore.getState().user?.id) return null
        const prev = get()
        // Emit only on a real transition (and not the first-ever load), so
        // cross-platform / webhook-driven Pro changes are captured once.
        if (prev.loaded && prev.isPro !== ent.isPro) {
          track(AnalyticsEvent.ProStatusChanged, {
            is_pro: ent.isPro,
            source: ent.source,
          })
        }
        apply(ent, owner)
        return ent
      },
      confirmPurchase: async () => {
        const owner = useAuthStore.getState().user?.id ?? null
        set({ isPro: true, loaded: true, ownerId: owner })
        const run = async () => {
          const res = await apiFetch<ReconcileResponse>('/subscription/reconcile', {
            method: 'POST',
          })
          // Only a Pro answer is news: RevenueCat already confirmed this
          // purchase, so anything else is a lookup that failed, and the
          // optimistic unlock stands until the webhook lands.
          if (res?.entitlement.isPro && owner && owner === useAuthStore.getState().user?.id) {
            apply(res.entitlement, owner)
          }
        }
        confirming = run().finally(() => {
          confirming = null
        })
        await confirming
      },
      reset: () => {
        set({
          isPro: false,
          loaded: false,
          expiresAt: null,
          willRenew: false,
          source: 'none',
          plan: null,
          noteLimit: null,
          ownerId: null,
        })
        // A device with no account is free by definition: `is_pro` is server
        // truth and there is no server row. Writing that explicitly, on every
        // signed-out start and after every logout, is what gives a local-mode
        // user a profile at all, so `undefined` in Mixpanel means telemetry
        // never ran rather than "free user we never labelled". Mirrors Swift's
        // `TelemetryRuntime.reset()`.
        setProfileProps({ is_pro: false, plan_source: 'none', plan: 'none' })
      },
    }),
    {
      name: 'caliu-subscription',
      storage: createJSONStorage(() => localStorage),
      // Persist the entitlement data, not the action functions.
      partialize: (state) => ({
        isPro: state.isPro,
        loaded: state.loaded,
        expiresAt: state.expiresAt,
        willRenew: state.willRenew,
        source: state.source,
        plan: state.plan,
        noteLimit: state.noteLimit,
        ownerId: state.ownerId,
      }),
    },
  ),
)

/** Write a server answer into the store and the profile properties. */
function apply(ent: Entitlement, owner: string): void {
  useSubscriptionStore.setState({
    isPro: ent.isPro,
    expiresAt: ent.expiresAt,
    willRenew: ent.willRenew,
    source: ent.source,
    // Older workers omit these; `?? null` keeps the state shape stable.
    plan: ent.plan ?? null,
    noteLimit: ent.noteLimit ?? null,
    loaded: true,
    ownerId: owner,
  })
  // Keep the profile segmentation attributes fresh (never event props).
  // `plan` is `subscription | lifetime | none` per ANALYTICS.md.
  setProfileProps({
    is_pro: ent.isPro,
    plan_source: ent.source,
    plan: ent.plan ?? 'none',
  })
}

// Another tab's sign-out, purchase or refresh lands here too, instead of this
// tab writing its stale copy back over it.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === 'caliu-subscription') void useSubscriptionStore.persist.rehydrate()
  })
}

/**
 * The Pro boolean the app gates on. Until the entitlement has ever been fetched
 * (`!loaded` — only the very first app use, a fresh device, or right after
 * logout) this reads `true` so a real Pro user is never locked out of a feature
 * before we know their status. The codebase contract is "leaking a Pro feature
 * is acceptable; breaking one is not." Once loaded (persisted), it is the real
 * entitlement.
 *
 * The fail-open only applies when a server-backed user exists (`hasUser`):
 * a session with no user can never be Pro, and `GET /subscription` will never
 * resolve for it, so `!loaded` would otherwise read as Pro forever. Pure so
 * the gating rule is unit-testable.
 */
export function selectIsPro(
  state: Pick<SubscriptionState, 'isPro' | 'loaded'>,
  hasUser: boolean,
): boolean {
  return state.isPro || (!state.loaded && hasUser)
}

/**
 * Subscribe to the Pro boolean the app gates on — the real server entitlement
 * (grandfather flag + RevenueCat state), restored from localStorage on start.
 */
export const useIsPro = () => {
  const hasUser = useAuthStore((state) => state.user !== null)
  return useSubscriptionStore((state) => selectIsPro(state, hasUser))
}
